import React, { useState } from 'react'

interface CloseModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (resolutionTag: string, notes: string) => void
  loading?: boolean
}

const RESOLUTION_TAGS = [
  { value: 'Resolved', color: 'bg-success-light text-green-700 dark:text-green-300 border-green-200 dark:border-green-800' },
  { value: 'Escalated', color: 'bg-warning-light text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800' },
  { value: 'No Response', color: 'bg-surface-tertiary text-txt-secondary border-border' },
  { value: 'Duplicate', color: 'bg-surface-tertiary text-txt-secondary border-border' },
  { value: 'Feature Request', color: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800' },
]

export const CloseModal: React.FC<CloseModalProps> = ({ isOpen, onClose, onConfirm, loading = false }) => {
  const [selectedTag, setSelectedTag] = useState('Resolved')
  const [notes, setNotes] = useState('')

  const handleConfirm = () => {
    onConfirm(selectedTag, notes)
    setSelectedTag('Resolved')
    setNotes('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" />

      <div
        className="relative w-full max-w-md bg-surface-elevated rounded-xl shadow-float border border-border p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-error-light">
            <svg className="w-5 h-5 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-txt-primary">Close conversation</h2>
            <p className="text-sm text-txt-secondary">Select a resolution and add notes if needed</p>
          </div>
        </div>

        {/* Resolution tags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-txt-primary mb-2">Resolution</label>
          <div className="flex flex-wrap gap-2">
            {RESOLUTION_TAGS.map((tag) => (
              <button
                key={tag.value}
                onClick={() => setSelectedTag(tag.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  selectedTag === tag.value
                    ? `${tag.color} ring-2 ring-brand-500/30`
                    : 'bg-surface border-border text-txt-secondary hover:border-border-strong'
                }`}
              >
                {tag.value}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-txt-primary mb-1.5">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add internal notes about this conversation..."
            rows={3}
            className="w-full px-3 py-2.5 rounded-lg bg-surface border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 resize-none transition-colors"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium text-txt-secondary hover:bg-surface-hover border border-border transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-error text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Closing...' : 'Close conversation'}
          </button>
        </div>
      </div>
    </div>
  )
}
